import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import MonthRail from './MonthRail';
import SeasonCropCard from './SeasonCropCard';
import SeasonalOverview from './SeasonalOverview';
import { RegionPicker } from '@/components/RegionPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ value, label, color }) {
  return (
    <div className="text-center">
      <div className="text-xl font-bold" style={{ color, fontFamily: 'Outfit, sans-serif' }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#7A6B57]">{label}</div>
    </div>
  );
}

// Full-screen Seasonal Planting Template overlay (web). Region tag + month rail +
// live catalogue + tick-and-track + pinned summary that opens the season overview.
export default function SeasonalTemplateOverlay({ user, onClose }) {
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fade, setFade] = useState(true);
  const [regionOpen, setRegionOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);

  const load = useCallback(async (m, soft) => {
    if (!soft) setLoading(true);
    try {
      const r = await axios.get(`${API}/planting-template?month=${m}`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData((prev) => prev || { crops: [] });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(month); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMonth = (m) => {
    setMonth(m);
    setFade(false);
    load(m, true).then(() => setTimeout(() => setFade(true), 30));
  };

  const cycle = async (crop, next) => {
    setData((prev) => {
      if (!prev) return prev;
      const crops = prev.crops.map((c) => (c.crop_id === crop.crop_id ? { ...c, status: next } : c));
      return { ...prev, crops, planned: crops.filter((c) => c.status === 'planned').length, planted: crops.filter((c) => c.status === 'planted').length };
    });
    try { await axios.put(`${API}/planting-plan`, { crop_id: crop.crop_id, month, status: next || 'none' }, { withCredentials: true }); } catch { /* keep optimistic */ }
  };

  const addToPlanner = async (crop) => {
    const sowing = new Date(new Date().getFullYear(), month - 1, 1).toISOString();
    try {
      await axios.post(`${API}/plantings`, { crop_id: crop.crop_id, crop_name: crop.name, stage: 'seed', sowing_date: sowing }, { withCredentials: true });
      await load(month, true);
    } catch { /* ignore */ }
  };

  const saveRegion = async (id) => {
    setRegionOpen(false);
    try { await axios.patch(`${API}/users/me`, { region: id }, { withCredentials: true }); await load(month); } catch { /* ignore */ }
  };

  const regionShort = (data?.region_label || '').split(' (')[0] || 'Set your region';
  const zoneTag = data?.zone_number ? ` · Zone ${data.zone_number}` : '';

  return (
    <div className="fixed inset-0 z-[70] overflow-hidden" data-testid="season-overlay" style={{ background: '#E6ECDC' }}>
      {/* soft harvest photo, heavily veiled so crop cards stay crisp + legible */}
      <div aria-hidden className="absolute inset-0" style={{
        backgroundImage: 'url(/harvest-basket.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.16,
      }} />
      <div aria-hidden className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(250,246,236,0.9) 0%, rgba(246,241,229,0.94) 55%, rgba(242,237,223,0.97) 100%)',
      }} />
      <div className="absolute inset-0 flex flex-col">
        <div className="px-4 sm:px-6 pt-5 pb-2 flex items-center gap-3 max-w-3xl mx-auto w-full">
          <button onClick={onClose} data-testid="season-close" className="hg-glass hg-press w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"><X className="w-5 h-5 text-[#1A4331]" /></button>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>What Can I Grow?</h2>
            <p className="text-xs text-[#5C665C]">Sow, transplant &amp; harvest for your region</p>
          </div>
        </div>

        <div className="px-4 sm:px-6 pb-2 max-w-3xl mx-auto w-full">
          <button onClick={() => setRegionOpen(true)} data-testid="season-region-tag" className="hg-glass hg-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-[#1A4331]">
            <MapPin className="w-3.5 h-3.5" /> {regionShort}{zoneTag} <ChevronDown className="w-3.5 h-3.5 text-[#7A6B57]" />
          </button>
        </div>

        <div className="px-4 sm:px-6 max-w-3xl mx-auto w-full"><MonthRail value={month} onChange={changeMonth} /></div>

        <div className="flex-1 overflow-y-auto hg-scroll px-4 sm:px-6 pb-40 pt-3 max-w-3xl mx-auto w-full">
          {loading ? (
            <div className="space-y-3">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="hg-glass rounded-2xl h-20 animate-pulse" />)}</div>
          ) : (
            <div className={`space-y-3 transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'}`}>
              {(data?.crops || []).length === 0 ? (
                <div className="hg-glass rounded-2xl p-8 text-center text-[#1A4331] font-medium">Nothing to sow outdoors this month for your zone. Try a warmer month.</div>
              ) : (data?.crops || []).map((c) => (
                <SeasonCropCard key={c.crop_id} crop={c} onCycle={(n) => cycle(c, n)} onAddToPlanner={() => addToPlanner(c)} onOpenPlanner={() => navigate('/my-crops')} />
              ))}
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-3xl mx-auto w-full p-4">
            <button onClick={() => setOverviewOpen(true)} data-testid="season-summary-bar" className="hg-glass-dock hg-press w-full rounded-2xl px-5 py-3 flex items-center gap-5" style={{ boxShadow: '0 8px 30px -14px rgba(26,67,49,0.5)' }}>
              <Stat value={data?.planted || 0} label="Planted" color="#6B9F4A" />
              <div className="w-px h-7 bg-[#1A4331]/10" />
              <Stat value={data?.planned || 0} label="Planned" color="#C9A85B" />
              <div className="w-px h-7 bg-[#1A4331]/10" />
              <Stat value={data?.available || 0} label="Available" color="#1A4331" />
              <ChevronRight className="w-5 h-5 text-[#7A6B57] ml-auto" />
            </button>
          </div>
        </div>
      </div>

      {regionOpen && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setRegionOpen(false)}>
          <div className="bg-[#FAF6EC] rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto hg-scroll" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-[#1A4331] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Change region</h3>
            <p className="text-sm text-[#5C665C] mb-4">Every sowing window retunes to your NZ climate zone.</p>
            <RegionPicker value={user?.region || ''} onChange={(id) => saveRegion(id)} testId="season-region-select" />
          </div>
        </div>
      )}

      {overviewOpen && <SeasonalOverview onClose={() => setOverviewOpen(false)} />}
    </div>
  );
}
